import { useState, useRef, useEffect } from 'react';

// Número WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = '5598981578332';

export default function Contact() {
  const sectionRef = useRef<HTMLDivElement>(null);

  // Estado do formulário — sem campo de telefone/WhatsApp
  const [formState, setFormState] = useState({
    nome: '',
    email: '',
    area: '',
    mensagem: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-contact').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Ao submeter:
   * 1. Impede o comportamento padrão do formulário (sem reload/fetch)
   * 2. Monta a mensagem com estrutura exata solicitada
   * 3. Codifica via encodeURIComponent
   * 4. Abre o WhatsApp em nova aba com a mensagem pronta
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Monta a mensagem com estrutura exata
    const mensagem =
      `Nome Completo: ${formState.nome} | ` +
      `E-mail: ${formState.email} | ` +
      `Área do Caso: ${formState.area} | ` +
      `Motivo do Contato: ${formState.mensagem}`;

    // Codifica para URL
    const mensagemCodificada = encodeURIComponent(mensagem);

    // Abre WhatsApp em nova aba
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${mensagemCodificada}`, '_blank');

    // Limpa o formulário após abertura
    setFormState({ nome: '', email: '', area: '', mensagem: '' });
  };

  const inputClass =
    'w-full bg-deep/60 border border-gold/20 focus:border-gold/60 text-ice font-sans text-sm font-300 px-4 py-3 rounded-md outline-none transition-colors duration-300 placeholder:text-ice/30';

  return (
    <section id="contato" ref={sectionRef} className="py-28 lg:py-36 bg-deep relative overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className="fade-contact text-center mb-20"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">Fale Conosco</span>
            <span className="w-10 h-px bg-gold" />
          </div>
          <h2 className="font-serif text-ice text-4xl lg:text-5xl font-700 mb-5">Contato</h2>
          <p className="font-sans text-ice/50 text-base font-300 max-w-lg mx-auto leading-relaxed">
            Entre em contato para agendar uma consulta ou esclarecer dúvidas. Atendemos com total sigilo e comprometimento.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Contact Info + Map */}
          <div
            className="fade-contact space-y-8"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}
          >
            {/* Info Cards */}
            <div className="space-y-5">
              {[
                {
                  icon: 'ri-map-pin-2-line',
                  title: 'Endereço',
                  content: 'Av. dos Holandeses, Edifício Century Multiempresarial\nSala 211, Quadra 11-A, Lote 14\nBairro Calhau — São Luís, MA',
                  link: 'https://maps.google.com/?q=Av.+dos+Holandeses+Calhau+São+Luís+MA',
                  linkLabel: 'Ver no mapa',
                },
                {
                  icon: 'ri-phone-line',
                  title: 'Telefone / WhatsApp',
                  content: '(98) 98157-8332',
                  link: 'tel:+5598981578332',
                  linkLabel: 'Ligar agora',
                },
                {
                  icon: 'ri-mail-line',
                  title: 'E-mail',
                  content: 'bennermartins.adv@gmail.com',
                  link: 'mailto:bennermartins.adv@gmail.com',
                  linkLabel: 'Enviar e-mail',
                },
                {
                  icon: 'ri-instagram-line',
                  title: 'Instagram',
                  content: '@benner_martins',
                  link: 'https://www.instagram.com/benner_martins',
                  linkLabel: 'Seguir no Instagram',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 p-5 border border-gold/10 hover:border-gold/30 rounded-md transition-colors duration-300"
                  style={{ background: 'rgba(13,27,42,0.4)' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border border-gold/30 rounded-md">
                    <i className={`${item.icon} text-gold text-lg`} />
                  </div>
                  <div>
                    <p className="font-sans text-gold/80 text-xs font-600 tracking-wider uppercase mb-1">{item.title}</p>
                    <p className="font-sans text-ice/70 text-sm font-300 leading-relaxed whitespace-pre-line">{item.content}</p>
                    <a
                      href={item.link}
                      target={item.link.startsWith('http') ? '_blank' : undefined}
                      rel={item.link.startsWith('http') ? 'nofollow noopener noreferrer' : undefined}
                      className="inline-flex items-center gap-1 text-gold hover:text-gold-light text-xs font-sans font-500 mt-1.5 transition-colors duration-300 cursor-pointer"
                    >
                      {item.linkLabel} <i className="ri-external-link-line text-xs" />
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Google Maps */}
            <div className="rounded-lg overflow-hidden border border-gold/15 h-56">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15946.921897!2d-44.294389!3d-2.498067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7f680b6bd0d5a69%3A0xc1e41c2db6dd3778!2sAv.%20dos%20Holandeses%2C%20S%C3%A3o%20Lu%C3%ADs%20-%20MA!5e0!3m2!1spt-BR!2sbr!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(70%) invert(90%)' }}
                allowFullScreen
                loading="lazy"
                title="Localização Benner Martins Advocacia - São Luís MA"
                aria-label="Mapa - Av. dos Holandeses, Calhau, São Luís MA"
              />
            </div>
          </div>

          {/* Right - Form */}
          <div
            className="fade-contact"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s' }}
          >
            <div className="p-8 lg:p-10 border border-gold/15 rounded-lg" style={{ background: 'rgba(13,27,42,0.5)' }}>
              <h3 className="font-serif text-ice text-xl font-600 mb-2">Envie sua Mensagem</h3>
              <p className="font-sans text-ice/40 text-sm font-300 mb-8">
                Preencha o formulário e você será direcionado ao WhatsApp com a mensagem pronta.
              </p>

              {/* Formulário — ao submeter abre WhatsApp, sem backend */}
              <form onSubmit={handleSubmit} className="space-y-4">

                {/* Nome Completo */}
                <div>
                  <label className="font-sans text-ice/50 text-xs font-500 tracking-wider uppercase mb-2 block">
                    Nome Completo *
                  </label>
                  <input
                    name="nome"
                    value={formState.nome}
                    onChange={handleChange}
                    required
                    type="text"
                    placeholder="Seu nome completo"
                    className={inputClass}
                  />
                </div>

                {/* E-mail */}
                <div>
                  <label className="font-sans text-ice/50 text-xs font-500 tracking-wider uppercase mb-2 block">
                    E-mail *
                  </label>
                  <input
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    required
                    type="email"
                    placeholder="seu@email.com"
                    className={inputClass}
                  />
                </div>

                {/* Área do Caso — opções editáveis diretamente no HTML */}
                <div>
                  <label className="font-sans text-ice/50 text-xs font-500 tracking-wider uppercase mb-2 block">
                    Área do Caso *
                  </label>
                  <select
                    name="area"
                    value={formState.area}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="" disabled>Selecione uma área</option>
                    {/* As opções abaixo são totalmente editáveis no HTML */}
                    <option value="Direito Penal">Direito Penal</option>
                    <option value="Direito Previdenciário">Direito Previdenciário</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="font-sans text-ice/50 text-xs font-500 tracking-wider uppercase mb-2 block">
                    Mensagem *{' '}
                    <span className="normal-case text-ice/30 font-300">
                      ({formState.mensagem.length}/500)
                    </span>
                  </label>
                  <textarea
                    name="mensagem"
                    value={formState.mensagem}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Descreva brevemente sua situação..."
                    className={`${inputClass} resize-none`}
                  />
                  {formState.mensagem.length >= 500 && (
                    <p className="text-red-400 text-xs mt-1 font-sans">Limite de 500 caracteres atingido.</p>
                  )}
                </div>

                {/* Botão — abre WhatsApp com mensagem codificada */}
                <button
                  type="submit"
                  disabled={formState.mensagem.length > 500}
                  className="w-full bg-gold hover:bg-gold-light disabled:opacity-50 text-deep font-sans font-600 text-sm py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer mt-2 flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line text-base" />
                  Enviar via WhatsApp
                </button>

                <p className="font-sans text-ice/25 text-xs text-center font-300 mt-3">
                  Todas as informações são tratadas com total sigilo e confidencialidade.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
